import { useState, useEffect } from 'react';
import { Plus, Search, BookmarkPlus, Sparkles, Loader2, CheckSquare, X, Trash2, Archive, FolderInput, CheckCheck, Bot, Wand2 } from 'lucide-react';
import { useDebounce } from '../hooks/use-debounce';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import { bookmarksApi } from '../lib/api';
import { extractDomain, getFaviconUrl } from '../lib/metadata';
import { getIconByName } from '../lib/icons';
import { TagInput } from './TagInput';
import { useTags } from '../hooks/use-tags';
import { quickEnrich, isAIEnrichmentAvailable } from '../lib/bookmark-enrichment';
import { generateAITags, generateAISummary } from '../lib/ai';
import type { Collection } from '../types/database';

interface TopBarProps {
  collections: Collection[];
  onBookmarkAdded: () => void;
  onSearch: (query: string) => void;
  resultCount?: number;
  isSearching?: boolean;
  // Selection mode props
  selectionMode?: boolean;
  onToggleSelectionMode?: () => void;
  selectedCount?: number;
  totalCount?: number;
  onSelectAll?: () => void;
  onBulkMove?: (collectionId: string | null) => void;
  onBulkDelete?: () => void;
  onBulkArchive?: () => void;
}

export function TopBar({
  collections,
  onBookmarkAdded,
  onSearch,
  resultCount,
  isSearching,
  selectionMode = false,
  onToggleSelectionMode,
  selectedCount = 0,
  totalCount = 0,
  onSelectAll,
  onBulkMove,
  onBulkDelete,
  onBulkArchive,
}: TopBarProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [aiTagging, setAiTagging] = useState(false);
  const [aiSummarizing, setAiSummarizing] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [notes, setNotes] = useState('');
  const [collectionId, setCollectionId] = useState<string>('');
  const [tags, setTags] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');

  const aiEnabled = isAIEnrichmentAvailable();

  // Debounce search input
  const debouncedSearch = useDebounce(searchInput, 300);

  // Trigger search when debounced value changes
  useEffect(() => {
    onSearch(debouncedSearch);
  }, [debouncedSearch, onSearch]);

  const { data: existingTags = [] } = useTags();

  const handleExtractMetadata = async () => {
    if (!url) return;

    setExtracting(true);
    try {
      // Use centralized enrichment module for metadata extraction
      const result = await quickEnrich(url);

      setTitle(result.metadata.title || url);
      setDescription(result.metadata.description || '');
      setContent(result.metadata.content || '');

      // Set auto-generated tags
      setTags(result.autoTags.join(', '));

      toast.success('정보를 가져왔어요');
    } catch (error) {
      console.error('Metadata extraction failed:', error);
      setTitle(url);
      toast.warning('정보를 가져오지 못했어요');
    } finally {
      setExtracting(false);
    }
  };

  const handleAITagging = async () => {
    if (!url || !title) {
      toast.error('먼저 URL과 제목을 입력해주세요');
      return;
    }

    if (!aiEnabled) {
      toast.error('AI 설정이 필요합니다. 설정에서 API 키를 입력해주세요.');
      return;
    }

    setAiTagging(true);
    try {
      const existingTagsList = tags.trim() ? tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const result = await generateAITags(url, title, description, existingTagsList);

      if (result.tags.length > 0) {
        // Merge AI tags with existing tags
        const allTags = new Set([...existingTagsList, ...result.tags]);
        setTags(Array.from(allTags).join(', '));
        toast.success(`AI가 ${result.tags.length}개의 태그를 추천했어요`);
      } else {
        toast.info('AI가 추가 태그를 찾지 못했어요');
      }
    } catch (error: any) {
      console.error('AI tagging failed:', error);
      toast.error(error.message || 'AI 태그 생성에 실패했어요');
    } finally {
      setAiTagging(false);
    }
  };

  const handleAISummary = async () => {
    if (!url || !title) {
      toast.error('먼저 URL과 제목을 입력해주세요');
      return;
    }

    if (!aiEnabled) {
      toast.error('AI 설정이 필요합니다. 설정에서 API 키를 입력해주세요.');
      return;
    }

    setAiSummarizing(true);
    try {
      const result = await generateAISummary(url, title, description);

      if (result.summary) {
        setExcerpt(result.summary);
        toast.success('AI가 요약을 생성했어요');
      } else {
        toast.info('AI가 요약을 생성하지 못했어요');
      }
    } catch (error: any) {
      console.error('AI summary failed:', error);
      toast.error(error.message || 'AI 요약 생성에 실패했어요');
    } finally {
      setAiSummarizing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url || !title) {
      toast.error('URL과 제목을 입력해주세요');
      return;
    }

    setLoading(true);
    try {
      const domain = extractDomain(url);
      const favicon = getFaviconUrl(url);

      // Prepare tags array
      const tagsList = tags.trim() ? tags.split(',').map(t => t.trim()).filter(Boolean) : [];

      // Create bookmark via API
      await bookmarksApi.create({
        url,
        title,
        description: description || null,
        content: content || null,
        excerpt: excerpt || null,
        domain,
        favicon_url: favicon,
        collection_id: collectionId && collectionId !== 'none' ? collectionId : null,
        notes: notes || null,
        tags: tagsList,
      });

      toast.success('저장했어요');
      setOpen(false);
      resetForm();
      onBookmarkAdded();
    } catch (error: any) {
      console.error('Error adding bookmark:', error);
      toast.error('저장에 문제가 생겼어요');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUrl('');
    setTitle('');
    setDescription('');
    setContent('');
    setExcerpt('');
    setNotes('');
    setCollectionId('');
    setTags('');
  };

  const renderCollectionIcon = (collection: Collection) => {
    const IconComponent = getIconByName(collection.icon || 'folder');
    return (
      <IconComponent 
        className="w-4 h-4 inline-block mr-1" 
        style={{ color: collection.color || '#10b981' }}
      />
    );
  };

  // Selection mode UI
  if (selectionMode) {
    return (
      <div className="h-14 border-b border-border bg-primary/5 flex items-center px-4">
        <div className="flex items-center justify-between w-full gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleSelectionMode}
              className="text-muted-foreground"
            >
              <X className="w-4 h-4 mr-1" />
              취소
            </Button>
            <div className="h-6 w-px bg-border" />
            <Button
              variant="ghost"
              size="sm"
              onClick={onSelectAll}
              className="text-foreground"
            >
              <CheckCheck className="w-4 h-4 mr-1.5" />
              {selectedCount === totalCount ? '전체 해제' : '전체 선택'}
            </Button>
            <span className="text-sm font-medium text-foreground">
              {selectedCount}개 선택됨
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Move to collection */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedCount === 0}
                >
                  <FolderInput className="w-4 h-4 mr-1.5" />
                  이동
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onBulkMove?.(null)}>
                  미분류로 이동
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {collections.map((collection) => (
                  <DropdownMenuItem
                    key={collection.id}
                    onClick={() => onBulkMove?.(collection.id)}
                  >
                    {renderCollectionIcon(collection)}
                    {collection.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Archive */}
            <Button
              variant="outline"
              size="sm"
              disabled={selectedCount === 0}
              onClick={onBulkArchive}
            >
              <Archive className="w-4 h-4 mr-1.5" />
              보관
            </Button>

            {/* Delete */}
            <Button
              variant="destructive"
              size="sm"
              disabled={selectedCount === 0}
              onClick={onBulkDelete}
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              삭제
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-14 border-b border-border bg-card flex items-center px-2 sm:px-4">
      <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
        <div className="relative flex-1 max-w-sm" data-tour="search">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="검색..."
            className="pl-9 pr-14 sm:pr-20 h-9"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {/* Search result count badge */}
          {searchInput && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
              {isSearching ? (
                <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
              ) : (
                <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  {resultCount ?? 0}개
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          {/* Selection mode toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleSelectionMode}
            className="text-muted-foreground"
            data-tour="bulk-select"
          >
            <CheckSquare className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">선택</span>
          </Button>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-tour="add-bookmark">
                <Plus className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">북마크 추가</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookmarkPlus className="w-5 h-5" />
                새 북마크 추가
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="url">URL *</Label>
                <div className="flex gap-2">
                  <Input
                    id="url"
                    type="url"
                    placeholder="https://example.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    required
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleExtractMetadata}
                    disabled={extracting || !url}
                    className="shrink-0"
                  >
                    {extracting ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-1.5" />
                    )}
                    {extracting ? '추출 중...' : '자동 추출'}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">제목 *</Label>
                <Input
                  id="title"
                  type="text"
                  placeholder="북마크 제목"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">설명</Label>
                <Textarea
                  id="description"
                  placeholder="북마크 설명 (선택사항)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="excerpt">AI 요약</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleAISummary}
                          disabled={aiSummarizing || !url || !title || !aiEnabled}
                          className="h-6 px-2 text-xs"
                        >
                          {aiSummarizing ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Wand2 className="w-3 h-3 mr-1" />
                          )}
                          요약 생성
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {aiEnabled
                          ? 'AI가 내용을 요약합니다'
                          : 'AI 설정이 필요합니다'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Textarea
                  id="excerpt"
                  placeholder="AI가 생성한 요약이 여기에 표시됩니다"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  rows={2}
                  className="text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="collection">컬렉션</Label>
                <Select value={collectionId} onValueChange={setCollectionId}>
                  <SelectTrigger>
                    <SelectValue placeholder="선택 (선택사항)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">없음</SelectItem>
                    {collections.map((collection) => (
                      <SelectItem key={collection.id} value={collection.id}>
                        {renderCollectionIcon(collection)}
                        {collection.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="tags">태그</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleAITagging}
                          disabled={aiTagging || !url || !title || !aiEnabled}
                          className="h-6 px-2 text-xs"
                        >
                          {aiTagging ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Bot className="w-3 h-3 mr-1" />
                          )}
                          AI 태그
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {aiEnabled
                          ? 'AI가 관련 태그를 추천합니다'
                          : 'AI 설정이 필요합니다'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <TagInput
                  value={tags}
                  onChange={setTags}
                  existingTags={existingTags}
                  placeholder="태그 입력 또는 선택"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">메모</Label>
                <Textarea
                  id="notes"
                  placeholder="개인 메모 (선택사항)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setOpen(false);
                    resetForm();
                  }}
                >
                  취소
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? '저장 중...' : '저장'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </div>
  );
}